<template>
  <div>
    <v-data-table
      :items-per-page="itemsPerPage"
      :no-data-text="$t('table.noResults')"
      :items="hostUserInvoices"
      :loading="isLoading"
      :headers="headers"
      hide-default-footer
      disable-sort
      show-expand
    >
      <template v-slot:item.real_estate="{ item }">
        <div v-html="customer(item)" />
      </template>
      <template v-slot:item.status="{ item }">
        <v-chip :color="color(item.status)" small>
          {{ $t(`host.label.asaas.status.${item.status}`) }}
        </v-chip>
      </template>
      <template v-slot:item.transfer="{ item }">
        <IconTooltip
          v-if="showTransferStatus(item.transfer)"
          :icon="transferStatusIcon(item.transfer)"
          :color="transferColorStatus(item.transfer)"
          :description="transferTextStatus(item.transfer)"
        />
        <span v-else class="caption">---</span>
      </template>
      <template v-slot:item.comment="{ item }">
        {{ comment(item) }}
      </template>
      <template v-slot:item.total="{ item }">
        {{ formatMoney(item.total) }}
      </template>
      <template v-slot:item.actions="{ item }">
        <v-menu offset-y bottom left>
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on">
              <v-icon small>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="openInvoice(item)">
              <v-icon class="mr-3 grey--text text--lighten-1" small left>mdi-eye-outline</v-icon>
              <v-list-item-title class="d-flex">
                {{ $t('hausefulInvoices.actions.show') }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item v-if="showResendInvoiceButton(item)" @click="resendInvoice(item)">
              <v-icon class="mr-3 grey--text text--lighten-1" small left>mdi-upload-box-outline</v-icon>
              <v-list-item-title class="d-flex">
                {{ $t('hausefulInvoices.actions.resendInvoice') }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:[`expanded-item`]="{ headers, item }">
        <td :colspan="headers.length" class="content pl-13 pb-10">
          <v-card class="rounded-b-lg" outlined>
            <v-data-table
              :items="item.payment_list"
              :headers="paymentHeaders"
              hide-default-footer
              disable-sort
            >
              <template v-slot:item.created_at="{ item }">
                {{ formatDate(item.created_at) }}
              </template>
              <template v-slot:item.code="{ item }">
                {{ codeCell(item) }}
              </template>
              <template v-slot:item.description="{ item }">
                {{ descriptionCell(item) }}
              </template>
              <template v-slot:item.total="{ item }">
                <span :class="moneyColor(item.total)">
                  {{ formatMoney(item.total) }}
                </span>
              </template>
            </v-data-table>
          </v-card>
        </td>
      </template>
    </v-data-table>

    <v-divider />

    <h-pagination :fetch="fetchHostUserInvoices" noBorders />
  </div>
</template>

<script>
import { handleColors } from '@/util/colors'
import { mapGetters, mapActions } from 'vuex'
import { catalogDate, catalogMonetary } from '@/util/catalog'
import { headers, paymentHeaders } from '../hostInvoiceHeaders'
import { customer } from '@/util/customer'

import IconTooltip from '@/components/IconTooltip'

export default {
  name: 'HostSentInvoices',

  components: {
    IconTooltip,
  },

  computed: {
    ...mapGetters(['isLoading', 'itemsPerPage', 'hostUserInvoices']),
  },

  methods: {
    ...mapActions(['openModal', 'fetchHostUserInvoices']),

    comment(item) {
      return item?.comment ?? '---'
    },

    codeCell(item) {
      return (item && item.appointment && item.appointment.code) || '---'
    },

    appointmentTypeLabel(item) {
      const type = item?.appointment?.type
      if (!type) return null

      const key = `appointment.type.${type}`
      const label = this.$t(key)
      if (label !== key) return label

      if (type === 'INSPECTION') return 'label.summary.survey'
      return type
    },

    descriptionCell(item) {
      return item?.description ?? this.appointmentTypeLabel(item) ?? '---'
    },

    transferStatusRaw(transfer) {
      return transfer ? transfer.status : undefined
    },

    transferStatusIcon(transfer) {
      const status = this.transferStatusRaw(transfer)

      return status !== 'COMPLETED' ? 'mdi-timer-sand' : 'mdi-check-bold'
    },

    transferTextStatus(transfer) {
      const status = this.transferStatusRaw(transfer)

      return status !== 'COMPLETED'
        ? this.$t('financial.payment.label.status.awaitingTransfer')
        : this.$t('financial.payment.label.status.transferred')
    },

    transferColorStatus(transfer) {
      const status = this.transferStatusRaw(transfer)

      const color = status !== 'COMPLETED' ? 'orange darken-3' : 'green darken-3'
      return color
    },

    showResendInvoiceButton(item) {
      return item.status === 'REFUSED'
    },

    showTransferStatus(transfer) {
      return transfer && transfer.status !== 'REFUSED' && transfer.status !== 'PENDING'
    },

    getPaymentsIds(payments) {
      return payments.map(payment => payment.id)
    },

    resendInvoice(invoice) {
      const invoice_id = invoice.id
      const payment_ids = this.getPaymentsIds(invoice.payment_list)
      const customer = {
        name: invoice.real_estate.name,
        total: invoice.total,
      }

      this.$emit('sendInvoiceData', { invoice_id, payment_ids, customer })
      this.openModal('RESEND_HOST_INVOICE_MODAL')
    },

    openInvoice(invoice) {
      const url = invoice?.file_response?.url
      window.open(url, '_blank')
    },

    moneyColor(value) {
      return value < 0 ? 'red--text text--lighten-1' : 'green--text text--lighten-1'
    },
  },

  data() {
    return {
      headers,
      customer,
      paymentHeaders,
      color: handleColors,
      formatDate: catalogDate,
      formatMoney: catalogMonetary,
    }
  }
}
</script>
